// Shared pointer-gesture bits (global pointer tracking + the .cell hit-test)
// used by the grid selection (selection.js), the preview panel (preview.js),
// layout.js and the fullscreen viewer (viewer.js) — kept here so none of those
// need to import each other.

// Media-type and sort-file constants shared by app.js and tree.js.
export const IMG = /\.(jpe?g|png|gif|webp|avif|bmp)$/i;
export const VID = /\.(mp4|webm|mov|m4v|ogv)$/i;
export const SORT_FILE = '.XnViewSort';
export const HEADER = 'XnView sort';

// Markup for a Lucide icon from the in-page sprite (the <svg id="iconSprite">
// in media-sorter.html) — for JS-built DOM via innerHTML, mirroring the static
// `<svg class="icon"><use href="#i-NAME"/></svg>` written directly in the HTML.
// `name` is a sprite symbol id without the `i-` prefix (e.g. 'chevron-down').
// Styled by the shared `.icon` rule (currentColor + em sizing) in styles.css.
export function icon(name) {
  return `<svg class="icon" aria-hidden="true"><use href="#i-${name}"/></svg>`;
}

// How far the pointer travelled since it went down, and what kind of pointer
// it is — tracked globally (capture phase) so every consumer agrees on
// "was this a tap or a scroll/drag".
export let ptrDown = null, ptrMoved = false, ptrType = '';
export function tapDist(e) { return ptrDown ? Math.hypot(e.clientX - ptrDown.x, e.clientY - ptrDown.y) : 0; }

addEventListener('pointerdown', (e) => {
  ptrDown = { x: e.clientX, y: e.clientY }; ptrMoved = false; ptrType = e.pointerType;
}, true);
addEventListener('pointermove', (e) => {
  if (ptrDown && Math.hypot(e.clientX - ptrDown.x, e.clientY - ptrDown.y) > 10) ptrMoved = true;
}, true);

// Toggle browser fullscreen on the document element. Shared by the toolbar
// ⛶ button / grid `F` (app.js) and the viewer's `F` (viewer.js) — pure logic,
// no shared state, so it lives here rather than being copied in both.
export function toggleBrowserFullscreen() {
  if (document.fullscreenElement) document.exitFullscreen();
  else document.documentElement.requestFullscreen().catch(() => {});
}

// While in browser fullscreen, capture Escape (Keyboard Lock API) so the app's
// own Esc handlers — viewer close, tree/context-menu and dialog cancel, … —
// fire consistently instead of the browser eating the first Esc to exit
// fullscreen (which otherwise takes a confusing *second* Esc to reach the app).
// With the lock held, Chrome moves its exit-fullscreen gesture to press-and-hold
// Escape (it shows its own overlay hint); short Esc is delivered as a normal
// keydown. Chromium-only — Firefox/Safari lack the API, so the optional chain
// no-ops and they keep the browser default (first Esc exits fullscreen). The
// lock auto-releases when fullscreen ends, so we only ever re-lock on enter,
// keyed off fullscreenchange so it covers every entry path (toolbar ⛶, grid/
// viewer `F`/`f`).
addEventListener('fullscreenchange', () => {
  if (document.fullscreenElement) navigator.keyboard?.lock?.(['Escape'])?.catch(() => {});
});

// The .cell under a viewport point, nudging a few px past the 2px inter-cell
// gap where elementFromPoint can land and miss. Shared by layout.js's pinch
// anchor and selection.js's range-select hit-testing.
export function cellAt(x, y) {
  for (const [dx, dy] of [[0, 0], [3, 0], [-3, 0], [0, 3], [0, -3]]) {
    const c = document.elementFromPoint(x + dx, y + dy)?.closest('.cell');
    if (c) return c;
  }
  return null;
}

// The visually adjacent cell one row above/below `fromCell`, chosen from
// `cells` (a candidate list — all grid children for cursor nav, the non-moving
// cells for a keyboard reorder). Justified-rows mode has no row structure in
// the DOM, so this works geometrically: find the nearest distinct row band in
// the requested direction (rows share a `top` since row height is uniform),
// then within it the cell whose horizontal center is closest to `fromCell`'s.
// Returns a cell or null when there is no row that way. Used by preview.js
// (Up/Down nav in rows mode) and app.js (Shift+Arrow reorder, both modes).
export function verticalNeighbor(fromCell, cells, dir) {
  const fr = fromCell.getBoundingClientRect();
  const fcx = fr.left + fr.width / 2;
  const down = dir === 'down';
  const rects = [];
  let bandTop = null;
  for (const c of cells) {
    if (c === fromCell) continue;
    const r = c.getBoundingClientRect();
    rects.push([c, r]);
    const beyond = down ? r.top > fr.top + 1 : r.top < fr.top - 1;
    if (!beyond) continue;
    if (bandTop === null || (down ? r.top < bandTop : r.top > bandTop)) bandTop = r.top;
  }
  if (bandTop === null) return null;
  let best = null, bestDx = Infinity;
  for (const [c, r] of rects) {
    if (Math.abs(r.top - bandTop) > 2) continue;
    const dx = Math.abs((r.left + r.width / 2) - fcx);
    if (dx < bestDx) { bestDx = dx; best = c; }
  }
  return best;
}
